import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Swal from 'sweetalert2';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

const parseIsoDateToLocal = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(year, monthIndex, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ConsultarInfoAlumno = () => {
  // Estados para los datos del alumno
  const [alumnoData, setAlumnoData] = useState({
    IdAlumno: '',
    Nombre: '',
    ApellidoPaterno: '',
    ApellidoMaterno: '',
    CURP: '',
    Sexo: '',
    Telefono: '',
    Correo: '',
    TipoSangre: '',
    FechaNacimiento: '',
    Grado: ''
  });

  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch {
            // mantener mensaje original
          }

          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Sistema dual de almacenamiento
  const getStorageItem = (key) => {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  };

  // Cargar información del alumno al montar el componente
  const cargarDatos = useCallback(async () => {
    try {
      const usuarioId = getStorageItem('idUsuario');

      console.log('🔍 Buscando ID de usuario en almacenamiento:', {
        sessionStorage: {
          idUsuario: sessionStorage.getItem('idUsuario'),
          id: sessionStorage.getItem('id')
        },
        localStorage: {
          idUsuario: localStorage.getItem('idUsuario'),
          id: localStorage.getItem('id')
        }
      });

      if (!usuarioId) {
        console.error('❌ No se encontró idUsuario en almacenamiento');
        alert('Error: No se encontró información del usuario logueado. Verifica el debug en pantalla.');
        setLoading(false);
        return;
      }

      console.log('🎓 Cargando información para usuario ID:', usuarioId);

      const { data } = await fetchJsonWithFallback(`/alumnos/mi-informacion/${usuarioId}`);
      console.log('✅ Datos del alumno cargados:', data);
      console.log('🔄 Estableciendo alumnoData con:', data);

      // Formatear fecha de nacimiento si existe
      if (data.FechaNacimiento) {
        const fecha = parseIsoDateToLocal(data.FechaNacimiento);
        data.FechaNacimiento = fecha ? fecha.toLocaleDateString('es-MX') : '';
      }

      setAlumnoData(data);
      console.log('✅ Estado alumnoData actualizado');
      setLoading(false);
    } catch (error) {
      console.error('❌ Error al cargar información:', error);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar la información del alumno'
      });
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const cargarInformacionAlumno = useCallback(async () => {
    setLoading(true);
    try {
      const usuarioId = getStorageItem('idUsuario');
      
      if (!usuarioId) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se encontró información del usuario logueado'
        });
        setLoading(false);
        return;
      }

      console.log('🎓 Recargando información para usuario ID:', usuarioId);

  const { data } = await fetchJsonWithFallback(`/alumnos/mi-informacion/${usuarioId}`);
  console.log('✅ Datos del alumno recargados:', data);

      // Formatear fecha de nacimiento si existe
      if (data.FechaNacimiento) {
        const fecha = parseIsoDateToLocal(data.FechaNacimiento);
        data.FechaNacimiento = fecha ? fecha.toLocaleDateString('es-MX') : '';
      }

      setAlumnoData(data);
  setLoading(false);
    } catch (error) {
      console.error('❌ Error al cargar información:', error);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar la información del alumno'
      });
    }
  }, [fetchJsonWithFallback]);

  // Manejar cambios en los inputs editables
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validación especial para teléfono
    if (name === 'Telefono') {
      // Solo permitir números y máximo 10 dígitos
      const numerosFiltrados = value.replace(/\D/g, '');
      if (numerosFiltrados.length <= 10) {
        setAlumnoData(prev => ({
          ...prev,
          [name]: numerosFiltrados
        }));
      }
      return;
    }

    setAlumnoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar modificación de información
  const handleModificar = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    try {
      const usuarioId = getStorageItem('idUsuario');

      // Validaciones
      if (!alumnoData.Correo || !alumnoData.Telefono) {
        Swal.fire({
          icon: 'warning',
          title: 'Campos requeridos',
          text: 'Por favor completa todos los campos obligatorios'
        });
        return;
      }

      if (alumnoData.Telefono.length !== 10) {
        Swal.fire({
          icon: 'warning',
          title: 'Teléfono inválido',
          text: 'El teléfono debe tener exactamente 10 dígitos'
        });
        return;
      }

      console.log('🔄 Enviando datos de modificación:', {
        usuarioId,
        correo: alumnoData.Correo,
        telefono: alumnoData.Telefono,
        tipoSangre: alumnoData.TipoSangre
      });

      const { data: result } = await fetchJsonWithFallback(`/alumnos/mi-informacion/${usuarioId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          correo: alumnoData.Correo,
          telefono: alumnoData.Telefono,
          tipoSangre: alumnoData.TipoSangre
        })
      });

      if (result && (result.error || result.success === false)) {
        throw new Error(result.error || result.message || 'Error al actualizar información');
      }

      console.log('✅ Información actualizada correctamente');

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Tu información ha sido actualizada correctamente'
      });

      setIsEditing(false);
    } catch (error) {
      console.error('❌ Error al modificar información:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo actualizar la información'
      });
    }
  };

  const handleCancelar = () => {
    setIsEditing(false);
    cargarInformacionAlumno(); // Recargar datos originales
  };

  if (loading) {
    return (
      <div className="container py-4 px-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando información...</p>
        
        {/* Debug info - más detallado */}
        <div className="mt-4 p-3 bg-light rounded small text-start">
          <h6>🔍 Debug - Información del sistema:</h6>
          <div className="mb-3">
            <strong>sessionStorage:</strong>
            <ul>
              <li>idUsuario: {sessionStorage.getItem('idUsuario') || 'NO ENCONTRADO'}</li>
              <li>role: {sessionStorage.getItem('role') || 'NO ENCONTRADO'}</li>
              <li>username: {sessionStorage.getItem('username') || 'NO ENCONTRADO'}</li>
              <li>role: {sessionStorage.getItem('role') || 'NO ENCONTRADO'}</li>
              <li>displayName: {sessionStorage.getItem('displayName') || 'NO ENCONTRADO'}</li>
              <li>token: {sessionStorage.getItem('token') ? 'presente' : 'NO ENCONTRADO'}</li>
            </ul>
          </div>
          <div>
            <strong>localStorage:</strong>
            <ul>
              <li>idUsuario: {localStorage.getItem('idUsuario') || 'NO ENCONTRADO'}</li>
              <li>role: {localStorage.getItem('role') || 'NO ENCONTRADO'}</li>
              <li>displayName: {localStorage.getItem('displayName') || 'NO ENCONTRADO'}</li>
              <li>token: {localStorage.getItem('token') ? 'presente' : 'NO ENCONTRADO'}</li>
            </ul>
          </div>
          <button 
            className="btn btn-sm btn-info mt-2"
            onClick={() => {
              console.log('🔍 Datos completos de almacenamiento:', {
                sessionStorage: Object.fromEntries(Object.entries(sessionStorage)),
                localStorage: Object.fromEntries(Object.entries(localStorage))
              });
            }}
          >
            Ver todos los datos en consola
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />

        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Información del Alumno</small>
        </div>

        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3" onSubmit={(e) => e.preventDefault()}>
          {/* Campos no editables */}
          <div className="col-md-3">
            <label htmlFor="idAlumno" className="form-label">ID Alumno</label>
            <input 
              type="text" 
              className="form-control" 
              id="idAlumno" 
              value={alumnoData.IdAlumno || ''} 
              disabled 
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="grado" className="form-label">Grado</label>
            <input 
              type="text" 
              className="form-control" 
              id="grado" 
              value={alumnoData.Grado || ''} 
              disabled
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="nombre" className="form-label">Nombre</label>
            <input 
              type="text" 
              className="form-control" 
              id="nombre" 
              name="Nombre"
              value={alumnoData.Nombre || ''} 
              onChange={handleInputChange}
              disabled 
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoPaterno" className="form-label">Apellido Paterno</label>
            <input 
              type="text" 
              className="form-control" 
              id="apellidoPaterno" 
              name="ApellidoPaterno"
              value={alumnoData.ApellidoPaterno || ''} 
              onChange={handleInputChange}
              disabled 
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoMaterno" className="form-label">Apellido Materno</label>
            <input 
              type="text" 
              className="form-control" 
              id="apellidoMaterno" 
              name="ApellidoMaterno"
              value={alumnoData.ApellidoMaterno || ''} 
              onChange={handleInputChange}
              disabled 
            />
          </div>

          {/* Campos no editables */}
          <div className="col-md-4">
            <label htmlFor="curp" className="form-label">CURP</label>
            <input 
              type="text" 
              className="form-control" 
              id="curp" 
              value={alumnoData.CURP || ''} 
              disabled 
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="sexo" className="form-label">Sexo</label>
            <input 
              type="text" 
              className="form-control" 
              id="sexo" 
              value={alumnoData.Sexo || ''} 
              disabled 
            />
          </div>

          {/* Campos editables */}
          <div className="col-md-6">
            <label htmlFor="correo" className="form-label">
              Correo <span className="text-danger">*</span>
            </label>
            <input 
              type="email" 
              className="form-control" 
              id="correo" 
              name="Correo"
              value={alumnoData.Correo || ''} 
              onChange={handleInputChange}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="tipoSangre" className="form-label">Tipo de Sangre</label>
            <select 
              className="form-control" 
              id="tipoSangre" 
              name="TipoSangre"
              value={alumnoData.TipoSangre || ''} 
              onChange={handleInputChange}
              disabled={!isEditing}
            >
              <option value="">Seleccionar...</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </div>

          <div className="col-md-3">
            <label htmlFor="telefono" className="form-label">
              Teléfono <span className="text-danger">*</span>
            </label>
            <input 
              type="text" 
              className="form-control" 
              id="telefono" 
              name="Telefono"
              value={alumnoData.Telefono || ''} 
              onChange={handleInputChange}
              disabled={!isEditing}
              placeholder="10 dígitos"
              maxLength="10"
              required
            />
          </div>

          {/* Fecha de nacimiento: campo no editable */}
          <div className="col-md-6">
            <label htmlFor="fechaNacimiento" className="form-label">Fecha de Nacimiento</label>
            <input 
              type="text" 
              className="form-control" 
              id="fechaNacimiento" 
              value={alumnoData.FechaNacimiento || ''} 
              disabled 
            />
          </div>
          
          <div className="col-md-12 text-end mt-3">
            {!isEditing ? (
              <button 
                type="button"
                className="btn btn-warning px-4" 
                onClick={handleModificar}
              >
                <i className="fa fa-edit me-2"></i>Modificar
              </button>
            ) : (
              <div>
                <button 
                  type="button"
                  className="btn btn-secondary me-2" 
                  onClick={handleCancelar}
                >
                  <i className="fa fa-times me-2"></i>Cancelar
                </button>
                <button 
                  type="button"
                  className="btn btn-success px-4" 
                  onClick={handleModificar}
                >
                  <i className="fa fa-save me-2"></i>Guardar
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultarInfoAlumno;
